/** Icon-free naming catalogue shared by the library UI and API validation.
 * Navigation identities, not renamed strings, identify each editable entry.
 * The UI parity test guards this catalogue against navigation drift. */
export const COST_NAV_NAMES: Array<{ id: string; label: string; items: Array<{ id: string; label: string }> }> = [
  { id: "preparation", label: "Preparation", items: [
    ["legal_fees", "Professional Fees"], ["site_setup", "Site Establishment"], ["scaffolding_general", "Access & Scaffolding"],
    ["equipment_hire", "Plant & Equipment"], ["welfare", "Welfare Facilities"], ["fences_barriers", "Fencing & Barriers"], ["preparation_protection", "Surface Protection"],
  ] },
  { id: "demolition_removal", label: "Demolition & Strip Out", items: [
    ["structural_demolition", "Structural Takedown"], ["strip_out", "Internal Strip Out"], ["waste_removal", "Waste & Disposal"],
  ] },
  { id: "drainage", label: "Drainage", items: [
    ["excavation", "Excavation & Earthworks"], ["drain_runs", "Pipework Runs"], ["aco_channels", "Linear Channels"], ["upvc_manhole", "Plastic Inspection Chambers"],
    ["brick_manholes", "Brick Chambers"], ["concrete_manholes", "Concrete Chambers"], ["soakaway", "Soakaway Systems"], ["septic_tank", "Off-Mains Drainage"],
  ] },
  { id: "foundation", label: "Foundations", items: [
    ["foundation", "Strip & Trench Fill"], ["underpinning", "Underpinning"], ["ground_screws", "Screw Pile Foundations"], ["ring_beam", "Ring Beam"], ["wall_formwork", "Formwork & Shuttering"],
  ] },
  { id: "below_dpc", label: "Substructure", items: [
    ["solid_walls_below_dpc", "Solid Masonry Below DPC"], ["cavity_walls_below_dpc", "Cavity Masonry Below DPC"], ["waterproofing", "Tanking & Waterproofing"], ["damp_proof_injection", "DPC Injection"],
  ] },
  { id: "ground_floor", label: "Ground Floor", items: [
    ["timber_floors", "Suspended Timber"], ["concrete_floors", "Concrete Slab"], ["beam_block_floors", "Beam & Block"], ["screed", "Floor Screed"],
  ] },
  { id: "walls", label: "Walls & Masonry", items: [
    ["external_walls", "External Masonry"], ["internal_walls", "Internal Blockwork"], ["internal_wall_insulation", "Wall Insulation (Internal)"], ["concrete_walls", "In-Situ Concrete"],
    ["stud_walls", "Timber Stud Partitions"], ["glass_block_wall", "Glass Block Walling"], ["columns", "Columns & Posts"], ["sip", "SIP Panels"], ["chimney", "Chimney Works"], ["toothing_out", "Brick Bonding"],
  ] },
  { id: "structural_elements", label: "Structural Elements", items: [
    ["steel_beams", "Steel RSJs"], ["timber_beams", "Timber Lintels & Beams"], ["padstones_connections", "Bearing Points & Fixings"], ["beam_connections", "Connections & Anchors"], ["fire_protection", "Steel Fire Protection"],
  ] },
  { id: "openings", label: "Doors & Windows", items: [
    ["windows", "Windows"], ["window_designer", "Bespoke Glazing"], ["external_doors", "External Doors"], ["garage_doors", "Garage Doors"], ["wall_openings", "New Openings"], ["lintels", "Lintels & Supports"],
  ] },
  { id: "upper_floor", label: "First Floor", items: [
    ["upper_floor_joists", "Floor Joist System"], ["beam_block_upper", "Beam & Block (Upper)"],
  ] },
  { id: "roof", label: "Roofing", items: [
    ["complete_roof", "Full Finish Roof"], ["roof_structure", "Roof Framing"], ["roof_trusses", "Trussed Rafters"], ["dormer", "Dormer Construction"],
    ["metal_roof_covering", "Metal Sheeting"], ["profile_range", "Roof Coverings"], ["flat_roof_covering", "Flat Roof Systems"], ["roof_windows", "Rooflights & Skylights"],
    ["roof_insulation", "Roof Insulation"], ["leadwork", "Leadwork & Flashings"], ["fascia_soffit", "Fascia, Soffit & Bargeboards"], ["rainwater_goods", "Gutters & Downpipes"],
  ] },
  { id: "electrics", label: "Electrical", items: [
    ["first_fix_electrics", "First Fix Wiring"], ["second_fix_electrics", "Second Fix Electrics"], ["testing_certification", "Testing & Certification"], ["incoming_electrical", "Mains Supply Connection"],
  ] },
  { id: "plumbing", label: "Plumbing", items: [
    ["plumbing_general", "First Fix Pipework"], ["advanced_plumbing", "Second Fix Plumbing"], ["drainage_plumbing", "Below Ground Drainage"], ["incoming_water", "Mains Water Connection"],
  ] },
  { id: "heating", label: "Heating & HVAC", items: [
    ["gas_boilers", "Boiler Systems"], ["incoming_gas", "Gas Supply Connection"], ["heating_general", "Central Heating"], ["cylinders", "Hot Water Cylinders"],
    ["radiators", "Radiators & Emitters"], ["ventilation_general", "Ventilation Systems"], ["air_conditioning", "Climate Control"], ["underfloor_heating", "Underfloor Heating"],
  ] },
  { id: "internal_finishes", label: "Internal Finishes", items: [
    ["tiling_walls", "Wall Tiling"], ["tiling_floors", "Floor Tiling"], ["plastering_skim", "Wet Plastering"], ["drylining", "Dry Lining"],
    ["wall_finishes", "Wall Treatments"], ["ceiling_finishes", "Ceiling Works"], ["floor_finishes", "Floor Coverings"], ["wall_panelling", "Timber Panelling"],
    ["cornice_coving", "Decorative Mouldings"], ["internal_cladding", "Interior Linings"], ["internal_decorating", "Painting & Decorating"], ["wallpapering", "Wallpapering"],
  ] },
  { id: "exterior_finishes", label: "External Finishes", items: [
    ["rendering", "Render Systems"], ["ext_wall_insulation", "EWI Systems"], ["external_cladding", "Facade Cladding"], ["external_decorating", "Exterior Painting"], ["masonry_painting", "Masonry Coatings"], ["glass_balustrades", "Glazed Balustrades"],
  ] },
  { id: "fixtures_installations", label: "Fixtures & Installations", items: [
    ["making_good_general", "Making Good"], ["snagging", "Snagging & Defects"], ["kitchen", "Kitchen"], ["bathroom", "Bathroom"],
  ] },
  { id: "carpentry_section", label: "Joinery & Carpentry", items: [
    ["first_fix_carpentry", "First Fix Carpentry"], ["internal_doors", "Door Replacement"], ["internal_doors_new", "New Door Installation"], ["joinery_workshop", "Bespoke Joinery"],
    ["stairs", "Staircases"], ["skirting_boards", "Skirting & Trim"], ["architraves", "Door Surrounds"], ["window_boards", "Sills & Boards"], ["loft_boarding", "Loft Flooring"], ["loft_hatch", "Loft Access"], ["boxing_in", "Pipe Boxing"],
  ] },
  { id: "other_section", label: "Other", items: [["other_general", "Miscellaneous"]] },
].map(section => ({
  ...section, items: section.items.map(([id, label]) => ({ id: id!, label: label! })),
}));

// Only genuinely corresponding quote headings share aliases. Broad filters and
// several independent library leaves must not rename one combined quote group.
export const NAV_SECTION_HEADING_KEYS: Record<string, string> = {
  preparation: "section:Preparation", demolition_removal: "section:Demolition",
  below_dpc: "section:Substructure", walls: "subgroup:masonry_works",
  structural_elements: "subgroup:structural_steel", roof: "section:Roofing",
  heating: "subgroup:heating", other_section: "section:Other",
};
const NAV_ITEM_QUOTE_KEYS: Record<string, string> = {
  "electrics:first_fix_electrics": "subgroup:first_fix_electrics",
  "electrics:second_fix_electrics": "subgroup:second_fix_electrics",
  "plumbing:plumbing_general": "subgroup:first_fix_plumbing",
  "plumbing:advanced_plumbing": "subgroup:second_fix_plumbing",
  "internal_finishes:drylining": "subgroup:drylining_works",
  "fixtures_installations:making_good_general": "subgroup:making_good",
  "roof:flat_roof_covering": "subgroup:flat_roofing",
  "roof:roof_windows": "subgroup:roof_windows",
};
export interface NavHeadingEntry { key: string; label: string; group: string; quoteKey?: string }
export function getNavHeadingKey(sectionId: string, itemId?: string): string {
  return itemId === undefined ? `nav:section:${sectionId}` : `nav:item:${sectionId}:${itemId}`;
}
export const COST_NAV_HEADING_CATALOG: NavHeadingEntry[] = COST_NAV_NAMES.flatMap(section => [
  { key: getNavHeadingKey(section.id), label: section.label, group: "Library categories", quoteKey: NAV_SECTION_HEADING_KEYS[section.id] },
  ...section.items.map(item => ({
    key: getNavHeadingKey(section.id, item.id), label: item.label, group: `Library subcategories:${section.id}`, quoteKey: NAV_ITEM_QUOTE_KEYS[`${section.id}:${item.id}`],
  })),
]);
export function getNavHeadingEntry(sectionId: string, itemId?: string): NavHeadingEntry {
  const key = getNavHeadingKey(sectionId, itemId);
  const entry = COST_NAV_HEADING_CATALOG.find(h => h.key === key);
  if (!entry) throw new Error("Unsupported category or subcategory.");
  return entry;
}
export function navHeadingDisplayLabel(entry: NavHeadingEntry, names?: Record<string, string> | null): string {
  return names?.[entry.key] || (entry.quoteKey && names?.[entry.quoteKey]) || entry.label;
}
export function getNavSectionDisplayLabel(section: { id: string; label: string }, names?: Record<string, string> | null): string {
  const entry = COST_NAV_HEADING_CATALOG.find(h => h.key === getNavHeadingKey(section.id));
  return entry ? navHeadingDisplayLabel(entry, names) : section.label;
}
export function getNavItemDisplayLabel(sectionId: string, item: { id: string; label: string }, names?: Record<string, string> | null): string {
  const entry = COST_NAV_HEADING_CATALOG.find(h => h.key === getNavHeadingKey(sectionId, item.id));
  return entry ? navHeadingDisplayLabel(entry, names) : item.label;
}
